// React & Hooks
import React, { useState } from "react";
import { Plus, Trash } from "lucide-react";
import api from "../../lib/api";
import useTheme from "../../hooks/useTheme";
import { useTranslation } from "react-i18next";

const AddYieldModal = ({ isOpen, onClose, onSaved, selectedDate }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  // States
  const [productName, setProductName] = useState("");
  const [mode, setMode] = useState("fixed");
  const [baseWeight, setBaseWeight] = useState("");
  const [boxes, setBoxes] = useState("");
  const [boxWeights, setBoxWeights] = useState([]);
  const [barrels, setBarrels] = useState([]);
  const [busy, setBusy] = useState(false);

  // Base button style
  const baseBtn =
    theme === "dark"
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-white text-black hover:bg-black hover:text-white"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-black text-white hover:bg-white hover:text-black";

  // Barrel handlers
  const addBarrel = () => setBarrels([...barrels, ""]);
  const updateBarrel = (i, v) => {
    const newList = [...barrels];
    newList[i] = v;
    setBarrels(newList);
  };
  const removeBarrel = (i) => {
    const newList = [...barrels];
    newList.splice(i, 1);
    setBarrels(newList);
  };

  // Box weights
  const addBoxWeight = () => setBoxWeights([...boxWeights, ""]);
  const updateBoxWeight = (i, v) => {
    const newList = [...boxWeights];
    newList[i] = v;
    setBoxWeights(newList);
  };
  const removeBoxWeight = (i) => {
    const newList = [...boxWeights];
    newList.splice(i, 1);
    setBoxWeights(newList);
  };

  // Calculations
  const totalBase =
    mode === "fixed"
      ? (parseFloat(baseWeight) || 0) * (parseInt(boxes) || 0)
      : boxWeights.reduce((s, b) => s + (parseFloat(b) || 0), 0);

  const totalObtained = barrels.reduce((s, b) => s + (parseFloat(b) || 0), 0);
  const weightDifference = totalObtained - totalBase;
  const yieldRatio = totalBase > 0 ? totalObtained / totalBase : 0;

  // Save
  const handleSave = async () => {
    if (!productName) return;
    setBusy(true);
    try {
      await api.post("/yields", {
        productName,
        date: selectedDate,
        baseWeight: mode === "fixed" ? parseFloat(baseWeight) || 0 : 0,
        boxes: mode === "fixed" ? parseInt(boxes) || 0 : boxWeights.length,
        totalBase,
        totalObtained,
        weightDifference,
        yieldRatio,
        mode,
        boxWeights: mode === "variable" ? boxWeights.map((b) => parseFloat(b) || 0) : [],
        barrels: barrels.map((b) => parseFloat(b) || 0),
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-lg font-bold mb-4">{t("addYield")}</h2>

        <input
          type="text"
          placeholder={t("productName")}
          className="w-full mb-2 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
        />

        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="w-full mb-2 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
        >
          <option value="fixed">{t("fixedWeight")}</option>
          <option value="variable">{t("variableWeight")}</option>
        </select>

        {mode === "fixed" ? (
          <>
            <input
              type="number"
              placeholder={t("kgPerBox")}
              className="w-full mb-2 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={baseWeight}
              onChange={(e) => setBaseWeight(e.target.value)}
            />
            <input
              type="number"
              placeholder={t("numberOfBoxes")}
              className="w-full mb-2 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={boxes}
              onChange={(e) => setBoxes(e.target.value)}
            />
          </>
        ) : (
          <>
            {boxWeights.map((w, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="number"
                  placeholder={`${t("box")} ${i + 1} (kg)`}
                  className="flex-1 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
                  value={w}
                  onChange={(e) => updateBoxWeight(i, e.target.value)}
                />
                <button
                  type="button"
                  className="px-2 text-red-600 font-bold"
                  onClick={() => removeBoxWeight(i)}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-blue-600 mb-2"
              onClick={addBoxWeight}
            >
              + {t("addBox")}
            </button>
          </>
        )}

        {barrels.map((w, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="number"
              placeholder={`${t("barrel")} ${i + 1} (kg)`}
              className="flex-1 p-2 border rounded bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={w}
              onChange={(e) => updateBarrel(i, e.target.value)}
            />
            <button
              type="button"
              className="px-2 text-red-600 font-bold"
              onClick={() => removeBarrel(i)}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          className="text-sm text-blue-600 mb-4"
          onClick={addBarrel}
        >
          + {t("addBarrel")}
        </button>

        <div className="mt-4 text-sm">
          <p>{t("totalBase")}: {totalBase.toFixed(2)} KG</p>
          <p>{t("totalObtained")}: {totalObtained.toFixed(2)} KG</p>
          <p>{t("weightDifference")}: {weightDifference.toFixed(2)} KG</p>
          <p>{t("yieldRatio")}: {yieldRatio.toFixed(3)}</p>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button className={baseBtn} onClick={onClose}>{t("cancel")}</button>
          <button className={baseBtn} onClick={handleSave} disabled={busy}>
            {busy ? t("saving") : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddYieldModal;